import { XMarkIcon } from "@heroicons/react/16/solid";
import { Heading, IconButton, ScrollArea, Theme } from "@radix-ui/themes";
import { AnimatePresence, motion, PanInfo, useAnimation } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import { snapPoints } from "react-spring-bottom-sheet/dist/types";
import { twMerge } from "tailwind-merge";

interface ResponsiveModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  title?: string | ReactNode;
  height?: string | number;
  maxHeight?: string | number;
  snapPoints?: snapPoints;
  header?: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
  className?: string;
  hideCloseButton?: boolean;
}

export const ResponsiveModal = ({
  isOpen,
  onClose,
  children,
  title,
  height = "50vh",
  maxHeight = "80vh",
  snapPoints,
  header,
  footer,
  className,
  contentClassName,
  hideCloseButton = false,
}: ResponsiveModalProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const controls = useAnimation();

  // Check if device is mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Handle open/close animations for desktop version
  useEffect(() => {
    if (isOpen) {
      controls.start({ y: 0, opacity: 1 });
    } else {
      controls.start({ y: "100%", opacity: 0 });
    }
  }, [isOpen, controls]);

  // Handle drag end for desktop version
  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.y > window.screen.width / 3 && onClose) {
      onClose?.();
    } else {
      controls.start({ y: 0, x: 0 });
    }
  };

  if (isMobile) {
    return (
      <BottomSheet
        open={isOpen}
        onDismiss={onClose}
        defaultSnap={({ maxHeight }) => maxHeight * 0.8}
        expandOnContentDrag
        className="bottom-sheet"
        header={
          <Theme
            accentColor="indigo"
            grayColor="slate"
            radius="full"
            scaling="100%"
            appearance={"inherit"}
          >
            <Heading size="3" style={{ margin: 0, padding: "16px" }}>
              {header || title}
            </Heading>
          </Theme>
        }
        footer={
          footer ? (
            <Theme
              accentColor="indigo"
              grayColor="slate"
              radius="full"
              scaling="100%"
              appearance={"inherit"}
            >
              {footer}
            </Theme>
          ) : undefined
        }
      >
        <Theme
          accentColor="indigo"
          grayColor="slate"
          radius="full"
          scaling="100%"
          appearance={"inherit"}
        >
          <div style={{ padding: "16px" }}>{children}</div>
        </Theme>
      </BottomSheet>
    );
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, pointerEvents: "none" }}
          animate={{ opacity: 1, pointerEvents: "all" }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          transition={{ duration: 0.2 }}
          className="bg-black/10 dark:bg-black/70 z-10"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            pointerEvents: isOpen ? "auto" : "none",
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={controls}
            exit={{ y: 50, opacity: 0 }}
            drag
            dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
            onDragEnd={handleDragEnd}
            className={twMerge(
              "bg-white dark:bg-slate-950 w-full max-w-2xl rounded-lg shadow-lg overflow-hidden flex flex-col mx-4",
              className
            )}
            style={{
              height: height,
              maxHeight: maxHeight,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {(header || title || onClose) && (
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                {(header || title) && (
                  <Heading size="3">{header || title}</Heading>
                )}
                {onClose && !hideCloseButton && (
                  <IconButton color="red" variant="ghost" onClick={onClose}>
                    <XMarkIcon className="w-6 h-6" />
                  </IconButton>
                )}
              </div>
            )}

            <ScrollArea className={twMerge("flex-1 p-4", contentClassName)}>
              {children}
            </ScrollArea>

            {footer && (
              <div
                className="border-t border-slate-200 dark:border-slate-700 p-4"
                onClick={(e) => e.stopPropagation()}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.querySelector("#root > div")!
  );
};
